import { OnInit, Component, ViewChild, ElementRef, HostListener, forwardRef, OnDestroy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'base-minute-input',
  templateUrl: './base-minute-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseMinuteInputComponent),
      multi: true,
    },
  ],
})
export class BaseMinuteInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  rawMinutes = 0;
  displayValue = '';
  isFocused = false;
  isDisabled = false;

  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};
  private arrowIntervalId: any = null;
  private mouseDown = false;

  @ViewChild('minuteInput', { static: true }) readonly minuteInputRef!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.updateDisplay();
  }

  ngOnDestroy(): void {
    this.stopArrowAction();
  }

  onFocus(): void {
    if (this.isDisabled) return;
    this.isFocused = true;
    setTimeout(() => {
      const input = this.minuteInputRef.nativeElement;
      input.setSelectionRange(0, input.value.length);
    });
  }

  onInput(event: Event): void {
    if (this.isDisabled) return;
    this.displayValue = (event.target as HTMLInputElement).value;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isFocused || this.isDisabled) return;

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.adjustMinutes(event.key === 'ArrowUp' ? 1 : -1);
    }
  }

  onBlur(): void {
    if (this.isDisabled) return;
    this.isFocused = false;
    this.rawMinutes = this.parseToMinutes(this.displayValue);
    this.updateDisplay();
    this.minuteInputRef.nativeElement.value = this.displayValue;
    this.onTouched();
    this.onChange(this.rawMinutes);
  }

  onArrowMouseDown(direction: 'up' | 'down'): void {
    if (this.isDisabled) return;
    this.mouseDown = true;
    this.bumpMinutes(direction);
    this.arrowIntervalId = setInterval(() => this.bumpMinutes(direction), 150);

    window.addEventListener('mouseup', this.onGlobalMouseUp);
  }

  onArrowMouseUp(): void {
    this.stopArrowAction();
  }

  onArrowMouseLeave(): void {
    if (this.mouseDown) {
      this.stopArrowAction();
    }
  }

  onGlobalMouseUp = () => {
    this.stopArrowAction();
  };

  private stopArrowAction(): void {
    this.mouseDown = false;

    if (this.arrowIntervalId) {
      clearInterval(this.arrowIntervalId);
      this.arrowIntervalId = null;
    }

    window.removeEventListener('mouseup', this.onGlobalMouseUp);
  }

  writeValue(value: number): void {
    this.rawMinutes = typeof value === 'number' ? value : 0;
    this.updateDisplay();
    if (!this.isFocused) {
      this.minuteInputRef.nativeElement.value = this.displayValue;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  private adjustMinutes(delta: number): void {
    this.rawMinutes = Math.max(0, this.parseToMinutes(this.displayValue) + delta);
    this.updateDisplay();
    this.minuteInputRef.nativeElement.value = this.displayValue;
    this.onChange(this.rawMinutes);
  }

  private bumpMinutes(direction: 'up' | 'down'): void {
    this.adjustMinutes(direction === 'up' ? 1 : -1);
  }

  private updateDisplay(): void {
    const h = Math.floor(this.rawMinutes / 60);
    const m = this.rawMinutes % 60;
    this.displayValue = `${h}h${m.toString().padStart(2, '0')}`;
  }

  private parseToMinutes(str: string): number {
    if (!str) return 0;
    str = str.trim().toLowerCase();

    const pureNum = /^\d+$/.exec(str);
    if (pureNum) return parseInt(pureNum[0], 10);

    const hhmm = /^(\d+)\s*h\s*(\d+)\s*m?$/.exec(str);
    if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);

    const mmOnly = /^(\d+)\s*m$/.exec(str);
    if (mmOnly) return parseInt(mmOnly[1], 10);

    const hOnly = /^(\d+)\s*h$/.exec(str);
    if (hOnly) return parseInt(hOnly[1], 10) * 60;

    return 0;
  }
}
